import type { ReactNode } from 'react';

import { cx } from '../cx';

export type Column<Row> = {
  /** Identifica a coluna (chave do React). */
  key: string;
  header: ReactNode;
  /** O que a celula mostra para cada linha. */
  cell: (row: Row) => ReactNode;
  align?: 'left' | 'right';
  /** Esconde a coluna abaixo de `md` (telemovel), para as tabelas caberem. */
  hideOnMobile?: boolean;
  /**
   * A coluna ocupa o espaco que sobra e corta o texto comprido com "…" em vez
   * de alargar a tabela (usar com `truncate` dentro da celula).
   */
  fill?: boolean;
};

export type TableProps<Row> = {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Descricao para o leitor de ecra (fica escondida no ecra). */
  caption: string;
  /** O que aparece quando nao ha linhas. */
  empty?: ReactNode;
  onRowClick?: (row: Row) => void;
  className?: string;
};

/** Tabela simples para listas do back-office (membros, encomendas, sessoes). */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  caption,
  empty = 'Sem resultados.',
  onRowClick,
  className,
}: TableProps<Row>) {
  return (
    // `relative` mantem os rotulos sr-only (position: absolute) dentro da caixa que
    // desliza; sem ele escapavam e alargavam a pagina no telemovel.
    <div className={cx('relative overflow-x-auto border border-gu-line', className)}>
      <table className="w-full border-collapse text-left text-body">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-gu-raised">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cx(
                  'border-b border-gu-line px-4 py-3 font-sub text-label font-bold uppercase text-gu-ink-muted',
                  column.align === 'right' && 'text-right',
                  column.hideOnMobile && 'hidden md:table-cell',
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-gu-ink-muted">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cx(
                  'border-b border-gu-line last:border-b-0',
                  onRowClick && 'cursor-pointer hover:bg-gu-raised',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cx(
                      'px-4 py-3 align-middle',
                      column.align === 'right' && 'text-right tabular-nums',
                      column.hideOnMobile && 'hidden md:table-cell',
                      // max-w-0 deixa a celula encolher; w-full da-lhe o resto da largura.
                      column.fill && 'w-full max-w-0',
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
